'use client';

import SvgChevronleft from '@common/icon/SvgChevronleft';
import SvgChevronright from '@common/icon/SvgChevronright';
import { useRef, useState } from 'react';

import type { CarouselApi } from '@/components/client';
import { HorizontalScroll, Image } from '@/components/client';

interface FestivalImageCarouselProps {
  images: string[];
  festivalName: string;
}

const PlaceholderImage = () => (
  <div
    role="img"
    aria-label="대표 이미지가 없습니다"
    className="flex h-[375px] w-full items-center justify-center bg-Gray-100 font-600 text-Gray-400 typo-body4"
  >
    이미지 준비 중
  </div>
);

interface ImageSlotProps {
  src: string;
  alt: string;
  preload?: boolean;
}

const ImageSlot = ({ src, alt, preload }: ImageSlotProps) => (
  <div className="relative h-[375px] min-w-full shrink-0 overflow-hidden bg-Gray-100">
    <Image
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 1280px) 100vw, 600px"
      className="object-cover"
      loading={preload ? 'eager' : 'lazy'}
      preload={preload}
      fetchPriority={preload ? 'high' : undefined}
      unoptimized
    />
  </div>
);

export const FestivalImageCarousel = ({
  images,
  festivalName,
}: FestivalImageCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<CarouselApi | null>(null);

  if (images.length === 0) return <PlaceholderImage />;

  if (images.length === 1) {
    return <ImageSlot src={images[0]} alt={festivalName} preload />;
  }

  return (
    <div className="relative w-full">
      <HorizontalScroll
        align="center"
        carouselRef={carouselRef}
        dragFree={false}
        loop
        onSlideChange={setActiveIndex}
      >
        {images.map((imageUrl, index) => (
          <ImageSlot
            key={imageUrl}
            src={imageUrl}
            alt={`${festivalName} ${index + 1}번째 이미지`}
            preload={index === 0}
          />
        ))}
      </HorizontalScroll>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-12">
        <button
          aria-label="이전 이미지"
          onClick={() => carouselRef.current?.scrollPrev()}
          className="pointer-events-auto grid size-40 place-items-center rounded-pill bg-Gray-black/50 text-Gray-white backdrop-blur-[2px] transition-colors hover:bg-Gray-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-Gray-white"
        >
          <SvgChevronleft className="size-20" />
        </button>
        <button
          aria-label="다음 이미지"
          onClick={() => carouselRef.current?.scrollNext()}
          className="pointer-events-auto grid size-40 place-items-center rounded-pill bg-Gray-black/50 text-Gray-white backdrop-blur-[2px] transition-colors hover:bg-Gray-black/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-Gray-white"
        >
          <SvgChevronright className="size-20" />
        </button>
      </div>
      <span
        role="status"
        aria-label={`이미지 페이지 표시 ${activeIndex + 1} / ${images.length}`}
        className="absolute bottom-16 right-16 inline-flex items-center rounded-pill bg-Gray-black/60 px-10 py-4 font-600 text-Gray-white backdrop-blur-[2px] typo-caption1"
      >
        {activeIndex + 1} / {images.length}
      </span>
    </div>
  );
};
